import { useEffect, useRef, useState } from 'react';
import { PrintDoc } from '../print/PrintDoc';

const aujourdHui = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Africa/Tunis', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());
const montant = (m) => `${(m / 1000).toLocaleString('fr-TN', {
  minimumFractionDigits: 3, maximumFractionDigits: 3,
})} TND`;
const input = 'h-11 w-full border border-slate-300 bg-white px-3';

export const ArreteCaisseJournalier = ({ comptes, onClose }) => {
  const caisses = comptes.filter((c) => c.type?.startsWith('Espèces'));
  const [date, setDate] = useState(aujourdHui);
  const [compteId, setCompteId] = useState(() => caisses.find((c) => c.id === 'chq')?.id || caisses[0]?.id || '');
  const [rapport, setRapport] = useState(null);
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(false);
  const [imprimer, setImprimer] = useState(false);
  const requete = useRef(null);
  useEffect(() => () => requete.current?.abort(), []);

  const charger = async () => {
    requete.current?.abort();
    const controller = new AbortController();
    requete.current = controller;
    setChargement(true); setErreur(''); setRapport(null);
    try {
      const qs = new URLSearchParams({ date, compte_id: compteId });
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finances/arrete-caisse?${qs}`, {
        credentials: 'include', cache: 'no-store', signal: controller.signal,
      });
      const body = await r.json();
      if (!r.ok) throw new Error(typeof body.detail === 'string' ? body.detail : 'Arrêté indisponible.');
      if (!controller.signal.aborted) setRapport(body);
    } catch (e) {
      if (e.name !== 'AbortError') setErreur(e.message);
    } finally {
      if (requete.current === controller) setChargement(false);
    }
  };
  const invalider = () => { requete.current?.abort(); setRapport(null); setErreur(''); setChargement(false); };

  if (imprimer && rapport) {
    const bloc = (sens, titre, total) => (
      <section className="mt-5" key={sens}>
        <h3 className="font-bold">{titre}</h3>
        <table className="mt-2 w-full border-collapse text-left text-xs" style={{ tableLayout: 'fixed' }}>
          <thead style={{ display: 'table-header-group' }}><tr>
            <th className="w-1/5 border-b p-2">Pièce / Référence</th>
            <th className="w-2/5 border-b p-2">Libellé / Bénéficiaire</th>
            <th className="w-1/5 border-b p-2">Mode</th>
            <th className="w-1/5 border-b p-2 text-right">Montant</th>
          </tr></thead>
          <tbody>{rapport.lignes.filter((l) => l.sens === sens).map((l, i) => (
            <tr key={`${l.origine}-${l.id}-${i}`} style={{ breakInside: 'avoid' }}>
              <td className="break-words border-b p-2">{l.piece || l.id || '—'}</td>
              <td className="break-words border-b p-2">{l.libelle}{l.beneficiaire ? ` · ${l.beneficiaire}` : ''}</td>
              <td className="break-words border-b p-2">{l.mode}</td>
              <td className="border-b p-2 text-right">{montant(l.montant_millimes)}</td>
            </tr>
          ))}</tbody>
        </table>
        {!rapport.lignes.some((l) => l.sens === sens) && <p>Aucun mouvement.</p>}
        <p className="mt-2 text-right font-bold">Total {titre.toLowerCase()} : {montant(total)}</p>
      </section>
    );
    return <PrintDoc type="Arrêté de caisse journalier" format="a4"
      numero={`AC-${rapport.compte.id}-${rapport.date}-v${rapport.version_registre}`}
      date={rapport.date} tiers={rapport.compte.nom}
      enfants={<><p className="mt-4 font-bold">Solde d’ouverture : {montant(rapport.ouverture_millimes)}</p>
        <p className="text-xs">Édité le {new Date(rapport.edite_le).toLocaleString('fr-TN', { timeZone: 'Africa/Tunis' })}</p>{bloc('entree', 'ENTRÉES', rapport.entrees_millimes)}{bloc('sortie', 'SORTIES', rapport.sorties_millimes)}</>}
      totaux={[
        ['Ouverture', montant(rapport.ouverture_millimes)],
        ['+ Entrées', montant(rapport.entrees_millimes)],
        ['− Sorties', montant(rapport.sorties_millimes)],
        ['Clôture théorique', montant(rapport.cloture_millimes)],
      ]}
      mentions={rapport.mention}
      signatures={['Responsable caisse', 'Contrôle direction']}
      onClose={() => setImprimer(false)} />;
  }
  return <div className="fixed inset-0 z-50 overflow-auto bg-black/50 p-4" role="dialog" aria-modal="true" aria-label="Arrêté de caisse journalier">
    <div className="mx-auto mt-10 max-w-xl space-y-4 bg-white p-6">
      <h2 className="text-xl font-bold">Arrêté de caisse journalier</h2>
      <label className="block">Date
        <input type="date" value={date} max={aujourdHui()} disabled={chargement}
          onChange={(e) => { invalider(); setDate(e.target.value); }} className={input} />
      </label>
      <label className="block">Caisse
        <select value={compteId} disabled={chargement} onChange={(e) => { invalider(); setCompteId(e.target.value); }} className={input}>
          {caisses.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
        </select>
      </label>
      {erreur && <p role="alert" className="text-red-700">{erreur}</p>}
      {rapport && <dl className="space-y-2">{[
        ['Ouverture', rapport.ouverture_millimes], ['Entrées', rapport.entrees_millimes],
        ['Sorties', rapport.sorties_millimes], ['Clôture théorique', rapport.cloture_millimes],
      ].map(([k, v]) => <div key={k} className="flex justify-between"><dt>{k}</dt><dd>{montant(v)}</dd></div>)}</dl>}
      <div className="flex flex-wrap gap-3">
        <button type="button" className={input} disabled={chargement || !date || !compteId} onClick={charger}>
          {chargement ? 'Chargement…' : 'Calculer depuis le registre serveur'}
        </button>
        <button type="button" className={input} disabled={!rapport || chargement} onClick={() => setImprimer(true)}>Afficher le bordereau A4</button>
        <button type="button" className={input} onClick={onClose}>Fermer</button>
      </div>
    </div>
  </div>;
};
